"use client";

import React from "react";
import { Calendar, User, Tag, ArrowLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-10 bg-muted/20 rounded-md w-3/4 mx-auto"></div>
        <div className="h-6 bg-muted/20 rounded-md w-1/2 mx-auto"></div>
        <div className="h-[400px] bg-muted/20 rounded-md w-full"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted/20 rounded-md w-full"></div>
          <div className="h-4 bg-muted/20 rounded-md w-full"></div>
          <div className="h-4 bg-muted/20 rounded-md w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-default">Failed to load production details.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground font-default">Project not found.</p>
        <Link to="/HomePage" className="mt-4 inline-block">
          <Button variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">Return to Studio</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="text-center mb-10">
        <div className="mb-6 flex justify-center gap-2 flex-wrap">
          {post.categories && post.categories.length > 0 && post.categories.map((cat, i) => (
            <span key={cat.id} data-index={i} className="text-xs font-default font-medium uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground leading-tight mb-6 uppercase tracking-tighter">
          {post.title}
        </h1>
        
        <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground font-mono">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </time>
            </div>
          )}
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-md overflow-hidden shadow-xl relative aspect-video bg-muted/10 grayscale hover:grayscale-0 transition-all duration-700">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {}
      <div className="bg-card border border-border rounded-md p-6 md:p-12 shadow-md">
        <div 
          className="prose prose-invert prose-lg max-w-none text-muted-foreground leading-relaxed font-sans [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:text-foreground [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:uppercase [&>h2]:tracking-tight [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-semibold [&>h3]:text-foreground [&>h3]:mt-8 [&>h3]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>blockquote]:border-l-2 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-foreground/90 [&>blockquote]:my-8 [&>blockquote]:font-serif [&>img]:rounded-md [&>img]:my-8 [&>img]:shadow-md [&>img]:grayscale [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/70"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-6 border-t border-border flex items-center gap-3 flex-wrap">
            <Tag className="w-5 h-5 text-muted-foreground" />
            {post.tags.map((tag, i) => (
              <span key={tag.id} data-index={i} className="text-xs font-mono text-muted-foreground bg-muted px-3 py-1 rounded-md hover:bg-accent transition-colors cursor-pointer uppercase">
                #{tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {}
      {post.author && (
        <Card className="mt-12 bg-card border-border shadow-lg rounded-md">
          <CardContent className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-6">
            <Avatar className="w-24 h-24 border border-border shadow-sm rounded-md">
              <AvatarImage src={post.author.avatar?.['96'] || "/img/producer_avatar_rhythm.png"} className="grayscale" />
              <AvatarFallback className="text-2xl font-default bg-muted text-primary rounded-md">
                {post.author.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left flex-1">
              <h3 className="text-2xl font-default font-bold text-foreground mb-2 uppercase tracking-tight">
                {post.author.name}
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed font-sans">
                Lead Producer at Rhythm & Soul Productions. Dedicated to sonic excellence, specializing in high-end mixing, mastering, and bespoke beat licensing for global artists.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null; 
  }

  return (
    <div className="mt-24">
      <div className="flex items-center justify-between mb-10">
        <h2 className="text-3xl font-default font-bold text-foreground uppercase tracking-tighter">Discography Highlights</h2>
        <Link to="/PortfolioPage">
          <Button variant="ghost" className="text-muted-foreground hover:text-primary font-mono text-xs uppercase tracking-widest">
            Full Portfolio <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.slice(0, 3).map((post, i) => (
          <div key={post.id} data-index={i}>
            <Link to={post.link || "#"} className="group block h-full">
              <Card className="h-full bg-card border-border hover:border-primary transition-all duration-500 hover:shadow-xl overflow-hidden flex flex-col rounded-md">
                {post.featuredImage && (
                  <div className="aspect-square overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-700">
                    <img 
                      src={post.featuredImage} 
                      alt={post.title} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                )}
                <CardHeader className="flex-1">
                  {post.categories && post.categories.length > 0 && (
                    <span className="text-[10px] font-mono font-medium text-muted-foreground mb-2 block uppercase tracking-widest">
                      {post.categories[0].name}
                    </span>
                  )}
                  <CardTitle className="text-lg font-default font-bold leading-tight group-hover:text-primary transition-colors line-clamp-2 uppercase tracking-tight">
                    {post.title}
                  </CardTitle>
                </CardHeader>
              </Card>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post' }; 
    
  const mainQuery = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="221814" id="single-post-template-section" className="bg-background text-foreground py-20 px-4 md:px-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        
        {}
        <div className="mb-12 max-w-4xl mx-auto">
          <Link to="/HomePage" className="inline-flex items-center text-xs font-mono font-medium text-muted-foreground hover:text-primary transition-colors uppercase tracking-widest">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Studio
          </Link>
        </div>

        {}
        <PostsProvider wp_query={mainQuery}>
          <MainPostConsumer />
        </PostsProvider>

        <Separator className="my-20 bg-border max-w-4xl mx-auto" />

        {}
        <div className="max-w-5xl mx-auto">
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>

      </div>
    </section>
  );
}